// Paso "Partido abierto" de la creación (T10): cuántos son ya, tipo de partido, quién lo puede ver y qué pasa
// si al cierre faltan jugadores. Es presentacional: el estado vive en `useCourtReservationFlow` (modo
// `openMatch`) y cada cambio sube por `onChange(patch)`. Muestra además el precio por jugador (turno ÷
// capacidad, solo informativo en v1: se paga en el club) y la hora de cierre (inicio del turno - closeHours).
import React from "react";
import RIcon from "../../reservas/components/ReservasIcons";
import {
	MATCH_KINDS,
	MATCH_KIND_LABELS,
	MATCH_VISIBILITIES,
	IF_NOT_FULL_OPTIONS,
	closeAtPreview,
	formatPricePerPlayer,
} from "../openMatchesModel";

const Choice = ({ active, onClick, label, hint }) => (
	<button type="button" className={`rmob-court-duration-pill${active ? " selected" : ""}`} onClick={onClick} aria-pressed={active}>
		<span className="duration">{label}</span>
		{hint && <span style={{ fontSize: 11, color: "#6b7280" }}>{hint}</span>}
	</button>
);

// `compact` (create flow, T17) tightens spacing so the step fits one viewport without page scroll.
const Section = ({ title, children, compact }) => (
	<div style={{ marginBottom: compact ? 10 : 20 }}>
		<h3 style={{ fontSize: compact ? 14 : 15, fontWeight: 800, marginBottom: compact ? 4 : 8 }}>{title}</h3>
		<div style={{ display: "flex", flexWrap: "wrap", gap: compact ? 6 : 8 }}>{children}</div>
	</div>
);

const OpenMatchRulesStep = ({ capacity, rules, closeHours, date, slotStart, price, onChange, compact = false }) => {
	const max = Math.max(1, capacity - 1);
	const missing = capacity - rules.alreadyPlayers;
	const close = closeAtPreview(date, slotStart, closeHours);
	return (
		<div data-testid="open-match-rules-step" className={compact ? "oma-rules-compact" : undefined}>
			<div style={{ marginBottom: compact ? 10 : 20 }}>
				<h3 style={{ fontSize: compact ? 14 : 15, fontWeight: 800, marginBottom: compact ? 4 : 8 }}>¿Cuántos son ya?</h3>
				<div style={{ display: "flex", alignItems: "center", gap: 14 }}>
					<button
						type="button"
						className="r-btn-soft"
						aria-label="Restar un jugador"
						disabled={rules.alreadyPlayers <= 1}
						onClick={() => onChange({ alreadyPlayers: rules.alreadyPlayers - 1 })}
					>
						−
					</button>
					<strong style={{ fontSize: 24, minWidth: 28, textAlign: "center" }}>{rules.alreadyPlayers}</strong>
					<button
						type="button"
						className="r-btn-soft"
						aria-label="Sumar un jugador"
						disabled={rules.alreadyPlayers >= max}
						onClick={() => onChange({ alreadyPlayers: rules.alreadyPlayers + 1 })}
					>
						+
					</button>
					<span style={{ fontWeight: 700 }}>{`Buscás ${missing} de ${capacity}`}</span>
				</div>
			</div>

			<Section compact={compact} title="Tipo de partido">
				{MATCH_KINDS.map((k) => (
					<Choice key={k} label={MATCH_KIND_LABELS[k]} active={rules.kind === k} onClick={() => onChange({ kind: k })} />
				))}
			</Section>

			<Section compact={compact} title="Quién lo puede ver">
				{MATCH_VISIBILITIES.map((v) => (
					<Choice key={v.value} label={v.label} hint={v.hint} active={rules.visibility === v.value} onClick={() => onChange({ visibility: v.value })} />
				))}
			</Section>

			<Section compact={compact} title="Si al cierre faltan jugadores">
				{IF_NOT_FULL_OPTIONS.map((o) => (
					<Choice key={o.value} label={o.label} active={rules.ifNotFull === o.value} onClick={() => onChange({ ifNotFull: o.value })} />
				))}
			</Section>

			<div style={{ padding: compact ? "8px 12px" : "12px 16px", borderRadius: 12, background: "#f9fafb", border: "1px solid #e5e7eb", fontSize: 14 }}>
				{price != null && capacity > 0 && (
					<div>
						<strong>{formatPricePerPlayer(Math.round(Number(price) / capacity))}</strong> por jugador · cada uno paga su parte en el club
					</div>
				)}
				{close && <div style={{ marginTop: 4, color: "#6b7280" }}>{close}</div>}
			</div>
		</div>
	);
};

export default OpenMatchRulesStep;
